import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useIdleTimeout } from "@/hooks/useIdleTimeout";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Konfigurasi auto-logout idle — ubah dua angka ini saja kalau durasinya
// dirasa kurang/kelamaan. IDLE_WARNING_MS harus lebih kecil dari IDLE_LOGOUT_MS.
const IDLE_WARNING_MS = 13 * 60 * 1000; // dialog peringatan muncul setelah 13 menit tanpa aktivitas
const IDLE_LOGOUT_MS = 15 * 60 * 1000; // logout otomatis setelah 15 menit tanpa aktivitas (total)
const COUNTDOWN_START_SECONDS = Math.round((IDLE_LOGOUT_MS - IDLE_WARNING_MS) / 1000);

function formatCountdown(totalSeconds) {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}

// Dipasang sekali di layout yang dipakai semua halaman ter-autentikasi (semua
// role), supaya sesi otomatis berakhir kalau komputer bersama ditinggal idle.
export default function IdleTimeoutManager() {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [showWarning, setShowWarning] = useState(false);
    const [countdown, setCountdown] = useState(COUNTDOWN_START_SECONDS);
    const countdownIntervalRef = useRef(null);

    const stopCountdown = useCallback(() => {
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
    }, []);

    const handleWarning = useCallback(() => {
        setCountdown(COUNTDOWN_START_SECONDS);
        setShowWarning(true);
        stopCountdown();
        countdownIntervalRef.current = setInterval(() => {
            setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);
    }, [stopCountdown]);

    const handleAutoLogout = useCallback(async () => {
        stopCountdown();
        setShowWarning(false);
        await logout();
        navigate("/login", { replace: true, state: { idleTimeout: true } });
    }, [logout, navigate, stopCountdown]);

    const { resetTimers } = useIdleTimeout({
        warningMs: IDLE_WARNING_MS,
        logoutMs: IDLE_LOGOUT_MS,
        onWarning: handleWarning,
        onLogout: handleAutoLogout,
    });

    useEffect(() => () => stopCountdown(), [stopCountdown]);

    const handleStaySignedIn = useCallback(() => {
        stopCountdown();
        setShowWarning(false);
        resetTimers();
    }, [stopCountdown, resetTimers]);

    return (
        <AlertDialog open={showWarning} onOpenChange={(open) => { if (!open) handleStaySignedIn(); }}>
            <AlertDialogContent
                data-testid="idle-timeout-dialog"
                // Dialog ini HANYA boleh ditutup lewat klik eksplisit tombol "Tetap
                // Masuk" (lihat komentar di useIdleTimeout.js) -- tanpa ini, Escape
                // (perilaku default Radix AlertDialog) akan memanggil onOpenChange(false)
                // di atas dan dianggap sama dengan klik tombol, padahal bukan konfirmasi
                // eksplisit dari user.
                onEscapeKeyDown={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
            >
                <AlertDialogHeader>
                    <AlertDialogTitle>Sesi akan segera berakhir</AlertDialogTitle>
                    <AlertDialogDescription data-testid="idle-timeout-countdown">
                        Sesi Anda akan berakhir dalam {formatCountdown(countdown)} menit karena tidak ada aktivitas.
                        Klik &quot;Tetap Masuk&quot; untuk melanjutkan.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogAction data-testid="idle-timeout-stay-button" onClick={handleStaySignedIn}>
                        Tetap Masuk
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
